import { useEffect, useMemo, useRef } from 'react';
import { ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import LandingScreen from '@/screens/LandingScreen';
import { RouteGateConnectionErrorState } from '@/components/route/RouteGate';
import { useAuth } from '@/hooks/useAuth';
import { getPendingInviteCode, getRedirectAfterLogin } from '@/lib/storage';
import { resolveAuthRedirect, resolvePostLoginHref, getLeagueIndexHref } from '@/lib/routes';
import { clientLogger } from '@/lib/datadog';
import { Box } from '@/components/ui/primitives';
import { useTheme } from '@/lib/styling';
import { Theme } from '@/theme';

export default function IndexRoute() {
  const router = useRouter();
  const { user, isAuthenticated, isLoading, hasConnectionError, retry } = useAuth();
  const hasResolvedRef = useRef(false);
  const theme = useTheme<Theme>();

  const firstLeagueId = useMemo(() => {
    if (!user?.leagues || user.leagues.length === 0) return null;
    const lid = user.defaultLeagueId ?? user.leagues[0]?.id;
    if (!lid || String(lid) === 'undefined') return null;
    return String(lid);
  }, [user]);

  const postLoginHref = useMemo(() => {
    if (firstLeagueId) {
      return getLeagueIndexHref(firstLeagueId);
    }
    return resolvePostLoginHref(user);
  }, [firstLeagueId, user]);

  useEffect(() => {
    if (isLoading || hasResolvedRef.current) {
      return;
    }

    let isMounted = true;

    const resolveRoute = async () => {
      if (!isAuthenticated) {
        return;
      }

      const [pendingInviteCode, redirectAfterLogin] = await Promise.all([
        getPendingInviteCode(),
        getRedirectAfterLogin(),
      ]);

      if (!isMounted) {
        return;
      }

      const redirectHref = resolveAuthRedirect(redirectAfterLogin, pendingInviteCode);
      
      if (hasResolvedRef.current) return;
      hasResolvedRef.current = true;
      
      if (__DEV__) {
        clientLogger.debug('[IndexRoute] Redirecting', { href: redirectHref ?? postLoginHref });
      }
      router.replace(redirectHref ?? postLoginHref);
    };

    void resolveRoute();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, isLoading, postLoginHref, router]);

  if (isLoading) {
    return (
      <Box flex={1} alignItems="center" justifyContent="center" backgroundColor="background">
        <ActivityIndicator size="large" color={theme.colors.press} />
      </Box>
    );
  }

  if (hasConnectionError) {
    return <RouteGateConnectionErrorState onRetry={retry} />;
  }

  return <LandingScreen />;
}
